import { poseSkyCathedralArtwork } from './114-sky-cathedral-art-geometry.mjs';
import { skyFlightFrameFromTangent, skyFlightPointToWorld } from './92-sky-flight-frame.mjs';
import {
  SKY_CATHEDRAL_SPACE as S,
  skyCathedralFrame,
  skyCathedralSolid,
  skyCathedralSupport,
  skyCathedralSweep,
} from './99-sky-cathedral-space.mjs';

const xyz = (x, y, z) => ({ x, y, z });
const add = (a, b) => xyz(a.x + b.x, a.y + b.y, a.z + b.z);
const sub = (a, b) => xyz(a.x - b.x, a.y - b.y, a.z - b.z);
const mul = (a, n) => xyz(a.x * n, a.y * n, a.z * n);
const dot = (a, b) => a.x * b.x + a.y * b.y + a.z * b.z;
const length = (a) => Math.hypot(a.x, a.y, a.z);
const unit = (a) => mul(a, 1 / length(a));
const clamp = (n, a, b) => Math.max(a, Math.min(b, n));
const smooth = (t) => {
  const x = clamp(t, 0, 1);
  return x * x * x * (10 + x * (-15 + 6 * x));
};

function beam(parts, id, a, b, width, kind = 'leg') {
  const delta = sub(b, a),
    size = Math.round(length(delta) * 1e9) / 1e9;
  const frame = skyFlightFrameFromTangent(mul(add(a, b), 0.5), delta, xyz(0, 0, 1));
  if (frame && size > 1e-6)
    parts.push(skyCathedralSolid(id, frame.origin, xyz(width, width, size / 2), kind, frame));
}
function portal(frame, center, halfWidth, halfHeight, normal = xyz(0, 0, 1)) {
  return {
    center: skyFlightPointToWorld(frame, center),
    normal: add(
      mul(frame.right, normal.x),
      add(mul(frame.up, normal.y), mul(frame.back, normal.z)),
    ),
    right: { ...frame.right },
    up: { ...frame.up },
    halfWidth,
    halfHeight,
  };
}
function localSolid(parts, chassis, id, center, half, kind = 'stone') {
  const frame = { ...chassis, origin: skyFlightPointToWorld(chassis, center) };
  const solid = skyCathedralSolid(id, frame.origin, half, kind, frame);
  parts.push(solid);
  return solid;
}
function architecture(parts, chassis, time, generatorOpen) {
  localSolid(parts, chassis, 'nave-floor', xyz(0, 13.5, 1.5), xyz(5.5, 0.5, 16.5));
  localSolid(parts, chassis, 'nave-roof', xyz(0, 24.5, 1.5), xyz(5.5, 0.5, 16.5));
  for (const side of [-1, 1]) {
    localSolid(parts, chassis, `nave-wall-${side}`, xyz(side * 5.5, 19, 1.5), xyz(0.5, 5, 16.5));
    for (const z of [-10, -3, 4, 11])
      localSolid(parts, chassis, `column-${side}-${z}`, xyz(side * 4.5, 19, z), xyz(0.5, 5, 0.5));
    localSolid(parts, chassis, `entrance-jamb-${side}`, xyz(side * 4.5, 19, -15), xyz(1.5, 5, 0.6));
    for (const z of [-8, 8])
      localSolid(
        parts,
        chassis,
        `copper-rod-${side}-${z}`,
        xyz(side * 5.4, 29, z),
        xyz(0.2, 4, 0.2),
        'copper',
      );
  }
  localSolid(parts, chassis, 'entrance-lintel', xyz(0, 23, -15), xyz(3, 1, 0.6));
  localSolid(parts, chassis, 'nave-end-wall', xyz(0, 19, 17.5), xyz(5, 5, 0.5));
  localSolid(parts, chassis, 'spire-main', xyz(0, 33.5, 4), xyz(2, 8.5, 2), 'spire');
  for (const side of [-1, 1]) {
    localSolid(
      parts,
      chassis,
      `spire-front-${side}`,
      xyz(side * 4, 29, -16),
      xyz(1, 4, 2),
      'spire',
    );
    const frame = skyCathedralFrame(
      skyFlightPointToWorld(chassis, xyz(side * 4, 26, -16)),
      Math.sin(time * 0.7 - 0.4) * 0.06,
    );
    parts.push(skyCathedralSolid(`bell-${side}`, frame.origin, xyz(0.5, 0.7, 0.5), 'bell', frame));
  }
  const generator = localSolid(
    parts,
    chassis,
    'generator',
    xyz(0, 18, 11),
    xyz(1, 1, 0.6),
    'target',
  );
  generator.componentId = 'generator';
  localSolid(
    parts,
    chassis,
    'generator-shutter',
    xyz(generatorOpen ? 3.2 : 0, 18, 10.2),
    xyz(1.2, 1.2, 0.2),
    'armor',
  );
  return {
    center: { ...generator.frame.origin },
    radius: 1,
    halfExtents: generator.halfExtents,
    frame: generator.frame,
    partId: generator.id,
    exposed: generatorOpen,
  };
}
function plantedFoot(id, index, gaitTime, travel) {
  const side = id.endsWith('left') ? -1 : 1,
    front = id.startsWith('front');
  // The middle pair takes separate one-second offsets between the retained
  // front/rear beats. Every planted sole stays fixed while its hip advances.
  const cycleTime = gaitTime + (id.startsWith('middle') ? (side < 0 ? 1 : 5) : index * 2),
    cycle = Math.floor(cycleTime / 8),
    phase = cycleTime - cycle * 8;
  const stride = 2.4,
    initial = 0;
  const swing = phase > 6 ? smooth((phase - 6) / 2) : 0;
  return {
    position: xyz(
      side * 13,
      5 + (phase > 6 ? Math.sin((Math.PI * (phase - 6)) / 2) ** 2 * 1.2 : 0),
      (front ? -14 : id.startsWith('middle') ? 0 : 14) +
        travel +
        initial -
        (cycle + swing) * stride,
    ),
    stance: phase <= 6,
  };
}
function solveLeg(hip, ankle, side) {
  const delta = sub(ankle, hip),
    distance = length(delta);
  if (distance > S.legLength * 2 || distance < 0.1) return null;
  const along = unit(delta),
    out = xyz(side, 0, 0);
  const bend = unit(sub(out, mul(along, dot(out, along))));
  const knee = add(
    mul(add(hip, ankle), 0.5),
    mul(bend, Math.sqrt(S.legLength ** 2 - distance ** 2 / 4)),
  );
  return { knee, kneeAngle: 2 * Math.asin(distance / (2 * S.legLength)) };
}
/** Shared provisional solid rig in metres. gaitTime advances the walking clock;
 * time drives secondary bells only. Capture lockPose at actual grounding and
 * hold gaitTime: locked joints/stance are reused exactly, never re-solved from
 * a running animation clock. sag/kneel require stopped gait and retained soles.
 * Invalid/out-of-court support returns valid:false with concrete failures. */
export function skyCathedralPose(options = {}) {
  const {
    time = 0,
    gaitTime = 0,
    travel = 0,
    lockedLegs = [],
    brokenLegs = [],
    damageProgress = {},
    openLegs = [],
    armorOpen = {},
    lockPose = null,
    sag = 0,
    kneel = 0,
    generatorOpen = false,
  } = options;
  if (![time, gaitTime, travel, sag, kneel].every(Number.isFinite) || gaitTime < 0)
    return { valid: false, failures: ['nonfinite-or-negative-clock'], parts: [] };
  const freeze = lockedLegs.length > 0 && lockPose?.valid && !sag && !kneel;
  const angle = -clamp(kneel, 0, 1) * S.maximumKneel;
  const walking = Math.min(gaitTime, 32),
    advance = walking * 0.3;
  const rear = ['rear-left', 'rear-right'].map((id, index) => {
    const leg = lockPose?.legs?.[id];
    const ankle =
      leg?.baseAnkle ?? leg?.ankle ?? plantedFoot(id, index + 2, walking, travel).position;
    return xyz(ankle.x, skyCathedralSupport(ankle.x, ankle.z)?.y ?? 4, ankle.z);
  });
  const pivot = mul(add(rear[0], rear[1]), 0.5),
    axis = unit(sub(rear[1], rear[0]));
  const rotate = (v) => {
    const cross = xyz(
      axis.y * v.z - axis.z * v.y,
      axis.z * v.x - axis.x * v.z,
      axis.x * v.y - axis.y * v.x,
    );
    return add(
      add(mul(v, Math.cos(angle)), mul(cross, Math.sin(angle))),
      mul(axis, dot(axis, v) * (1 - Math.cos(angle))),
    );
  };
  const base = {
    origin: pivot,
    right: rotate(xyz(1, 0, 0)),
    up: rotate(xyz(0, 1, 0)),
    back: rotate(xyz(0, 0, 1)),
  };
  const damage = (id) => (brokenLegs.includes(id) ? clamp(damageProgress[id] ?? 1, 0, 1) : 0);
  const leftFailure = damage('front-left'),
    rightFailure = damage('front-right');
  const loadDifference = leftFailure - rightFailure;
  const shift = loadDifference * 0.6;
  const drop = smooth((sag - 0.35) / 0.65) * (1 - kneel) * 1.2;
  const neutralOrigin = xyz(shift, 4 - drop, travel - advance + Math.abs(loadDifference) * 0.5);
  const origin = add(pivot, rotate(sub(neutralOrigin, pivot)));
  const chassis = freeze ? lockPose.chassis : { ...base, origin };
  const parts = [],
    legs = {},
    supports = [],
    failures = [],
    spireSockets = {};
  const generator = architecture(parts, chassis, time, generatorOpen);
  for (let index = 0; index < S.legIds.length; index++) {
    const id = S.legIds[index],
      side = id.endsWith('left') ? -1 : 1,
      front = id.startsWith('front'),
      anchorZ = front ? -12 : id.startsWith('middle') ? 0 : 12;
    const hip = skyFlightPointToWorld(chassis, xyz(side * 7, 13, anchorZ));
    localSolid(
      parts,
      chassis,
      `${id}-hip-crossmember`,
      xyz(side * 6, 13.2, anchorZ),
      xyz(1.5, 0.6, 0.7),
      'brace',
    );
    localSolid(
      parts,
      chassis,
      `${id}-hip-bearing`,
      xyz(side * 7, 13, anchorZ),
      xyz(0.85, 0.85, 0.85),
      'joint',
    );
    const retained = lockPose?.legs?.[id];
    const foot =
      retained && (freeze || sag > 0 || kneel > 0)
        ? {
            position: { ...(freeze ? retained.ankle : (retained.baseAnkle ?? retained.ankle)) },
            stance: freeze ? retained.stance : true,
          }
        : plantedFoot(id, index, walking, travel);
    if ((sag > 0 || kneel > 0) && !front) {
      foot.position.y = 5 + (foot.position.y - 5) * (1 - smooth(sag / 0.35));
      foot.stance = sag >= 0.35;
    }
    const baseAnkle = { ...foot.position };
    if (front && brokenLegs.includes(id) && !freeze) {
      foot.position = add(foot.position, mul(xyz(-side * 0.5, 0.8, 1), damage(id)));
      if (damage(id) > 0) foot.stance = false;
    }
    const solved =
      freeze && retained
        ? { knee: retained.knee, kneeAngle: retained.kneeAngle }
        : solveLeg(hip, foot.position, side);
    if (!solved) {
      failures.push(`${id}:unreachable-chain`);
      continue;
    }
    const { knee, kneeAngle } = solved,
      ankle = foot.position;
    beam(parts, `${id}-upper`, hip, knee, 1.8);
    beam(
      parts,
      `${id}-shin`,
      knee,
      ankle,
      1.5,
      front && brokenLegs.includes(id) ? 'fractured' : 'leg',
    );
    parts.push(
      skyCathedralSolid(
        `${id}-foot`,
        xyz(ankle.x, ankle.y + 0.2, ankle.z),
        xyz(1.5, 1.2, 2),
        'foot',
      ),
    );
    const sole = [-1, 1].flatMap((x) =>
      [-1, 1].map((z) => xyz(ankle.x + x * 1.5, ankle.y - 1, ankle.z + z * 2)),
    );
    for (const point of sole) {
      const support = skyCathedralSupport(point.x, point.z);
      if (!support || point.y < support.y - 1e-7)
        failures.push(`${id}:unsupported-or-penetrating-sole`);
      if (foot.stance && support && Math.abs(point.y - support.y) < 1e-7)
        supports.push({ partId: `${id}-foot`, point, supportId: support.id });
    }
    const targetCenter = add(knee, xyz(side * 2.45, 0, 0));
    const target = skyCathedralSolid(
      `${id}-mechanism`,
      targetCenter,
      xyz(0.35, 0.9, 0.9),
      'target',
    );
    target.componentId = id;
    parts.push(target);
    const armorFraction = clamp(armorOpen[id] ?? (openLegs.includes(id) ? 1 : 0), 0, 1);
    const armorCenter = add(targetCenter, xyz(side * 0.5, armorFraction * 2.2, 0));
    parts.push(skyCathedralSolid(`${id}-armor`, armorCenter, xyz(0.2, 1.1, 1.1), 'armor'));
    const catchpoint = add(knee, xyz(side * 4.4, 1.4, 0));
    beam(parts, `${id}-conductor-arm`, add(knee, xyz(0, 1.4, 0)), catchpoint, 0.15, 'copper');
    parts.push(skyCathedralSolid(`${id}-catchpoint`, catchpoint, xyz(0.45, 0.45, 0.45), 'copper'));
    const baitPocket = xyz(catchpoint.x, Math.max(6, catchpoint.y - 3), catchpoint.z);
    legs[id] = {
      hip,
      knee,
      ankle,
      baseAnkle,
      sole,
      kneeAngle,
      stance: foot.stance,
      broken: front && brokenLegs.includes(id),
      circuitDisabled: brokenLegs.includes(id),
      catchpoint: {
        center: catchpoint,
        radius: 0.45,
        halfExtents: xyz(0.45, 0.45, 0.45),
        partId: `${id}-catchpoint`,
      },
      baitPocket,
      target: {
        center: targetCenter,
        halfExtents: target.halfExtents,
        frame: target.frame,
        radius: 0.9,
        partId: target.id,
        exposed: openLegs.includes(id) && armorFraction === 1 && !brokenLegs.includes(id),
      },
    };
    spireSockets[id] = skyFlightPointToWorld(
      chassis,
      xyz(side * 5.4, 33, front ? -8 : id.startsWith('middle') ? 0 : 8),
    );
  }
  // Visible grounded arrest jacks carry the arrested chassis while fractured
  // front chains remain articulated. They stay outside the central 6m passage.
  for (const side of [-1, 1]) {
    const top = skyFlightPointToWorld(chassis, xyz(side * 7, 12.5, -10));
    localSolid(
      parts,
      chassis,
      `arrest-${side}-crossmember`,
      xyz(side * 6, 13, -10),
      xyz(1.4, 0.7, 0.7),
      'joint',
    );
    const ground = skyCathedralSupport(top.x, top.z);
    if (!ground || top.y < ground.y) {
      failures.push('arrest-jack-outside-foundation');
      continue;
    }
    const deploy = smooth(sag / 0.35);
    const foot = xyz(top.x, top.y - 1.35 + (ground.y + 0.35 - (top.y - 1.35)) * deploy, top.z);
    parts.push(skyCathedralSolid(`arrest-${side}-sole`, foot, xyz(1, 0.35, 1), 'brace'));
    const bottom = add(foot, xyz(0, 0.35, 0)),
      span = top.y - bottom.y;
    for (let segment = 0; segment < 16; segment++) {
      const start = ((span - 1) * segment) / 15;
      beam(
        parts,
        `arrest-${side}-jack-${segment}`,
        add(bottom, xyz(0, start, 0)),
        add(bottom, xyz(0, start + 1, 0)),
        0.35 - segment * 0.008,
        'brace',
      );
    }
    if (deploy === 1)
      supports.push({
        partId: `arrest-${side}-sole`,
        point: xyz(top.x, ground.y, top.z),
        supportId: ground.id,
      });
  }
  const underY = 7;
  const underStart = xyz(0, underY, -22 + travel - advance),
    underEnd = xyz(0, underY, 24 + travel - advance);
  const groundFrame = skyCathedralFrame(xyz(0, 0, travel - advance));
  const portals = {
    underpassEntry: portal(groundFrame, xyz(0, 7, -20), 3, 3),
    underpassExit: portal(groundFrame, xyz(0, 7, 22), 3, 3),
    naveEntry: portal(chassis, xyz(0, 18, -15.7), 3, 4),
  };
  const routes = {
    underpass: [underStart, underEnd],
    nave: [-20, -15, -10, -3, 5, 8].map((z) => skyFlightPointToWorld(chassis, xyz(0, 18, z))),
  };
  const result = {
    valid: failures.length === 0,
    failures,
    chassis,
    parts,
    legs,
    supports,
    portals,
    routes,
    spireSockets,
    generator: { ...generator, target: generator },
    waterY: S.waterY,
    kneel: clamp(kneel, 0, 1),
    sag: clamp(sag, 0, 1),
    supportPivot: { origin: pivot, axis, rearSoles: rear },
    floorCorners: [-5, 5].flatMap((x) =>
      [-15, 18].map((z) => skyFlightPointToWorld(chassis, xyz(x, 14, z))),
    ),
    safeExit: { center: underEnd, radius: 3 },
    gaitTime: walking,
  };
  return options.geometry ? poseSkyCathedralArtwork(options.geometry, result) : result;
}

/** Conservative bounded-step kneel collision for the protected collapse.
 * Only sag=1, stopped gait, immutable damage/sole state are interpolated. Steps
 * are <=0.25 degrees; each interval inflates the real sphere by twice the
 * measured maximum rigid vertex displacement, covering the substep motion.
 * This catches a roof sweeping across an otherwise stationary pilot, unlike
 * one local-frame endpoint ray. Invalid ranges return an explicit rejection. */
export function skyCathedralKneelSweep(
  from,
  to,
  options = {},
  start = 0,
  end = 1,
  radius = S.pilotRadius,
) {
  if (
    ![start, end, radius].every(Number.isFinite) ||
    start < 0 ||
    end > 1 ||
    end < start ||
    radius < 0
  )
    return { invalid: true, reason: 'invalid-kneel-range' };
  const steps = Math.max(1, Math.ceil((end - start) * 80));
  const interpolate = (a, b, t) => add(a, mul(sub(b, a), t));
  let previous = skyCathedralPose({ ...options, sag: 1, kneel: start });
  if (!previous.valid) return { invalid: true, reason: previous.failures.join(',') };
  for (let step = 1; step <= steps; step++) {
    const t0 = (step - 1) / steps,
      t1 = step / steps;
    const current = skyCathedralPose({ ...options, sag: 1, kneel: start + (end - start) * t1 });
    if (!current.valid) return { invalid: true, reason: current.failures.join(',') };
    let displacement = 0;
    for (let index = 0; index < current.parts.length; index++) {
      const a = previous.parts[index],
        b = current.parts[index];
      if (a.id !== b.id) return { invalid: true, reason: 'changed-collapse-topology' };
      const h = b.halfExtents;
      for (const x of [-h.x, h.x])
        for (const y of [-h.y, h.y])
          for (const z of [-h.z, h.z]) {
            const point = xyz(x, y, z);
            displacement = Math.max(
              displacement,
              length(
                sub(skyFlightPointToWorld(a.frame, point), skyFlightPointToWorld(b.frame, point)),
              ),
            );
          }
    }
    const hit = skyCathedralSweep(
      interpolate(from, to, t0),
      interpolate(from, to, t1),
      current,
      radius + displacement * 2 + 0.001,
    );
    if (hit)
      return {
        ...hit,
        fraction: t0 + hit.fraction / steps,
        motionMargin: displacement * 2 + 0.001,
      };
    previous = current;
  }
  return null;
}
